// src/pages/ResetPassword.jsx

import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabase/supabase-config';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import MfaChallenge from './admin/MfaChallenge';
import { nivelGarantia, fatorTotpVerificado } from '../services/mfa';

// Destino do link enviado por ForgotPassword.jsx (redirectTo: /reset-password).
// O Supabase já autentica a sessão a partir do token da URL antes de chegar
// aqui — não há token pra ler manualmente, só pedir a nova senha e chamar
// updateUser.
//
// Se a conta tem MFA cadastrado, a sessão de recovery vem em aal1 e o
// Supabase recusa updateUser({ password }) com "AAL2 session is required" —
// é proposital: sem isso, um e-mail comprometido bastaria pra derrubar o
// segundo fator. Por isso desafiamos o MFA aqui antes de liberar o formulário,
// mesmo padrão do AdminRoute.jsx.
const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [mfaEstado, setMfaEstado] = useState('checando'); // checando | desafiar | liberado
  const [mfaFactorId, setMfaFactorId] = useState(null);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const aal = await nivelGarantia();
        if (aal.currentLevel === 'aal2' || aal.nextLevel === 'aal1') {
          if (ativo) setMfaEstado('liberado');
          return;
        }
        const fator = await fatorTotpVerificado();
        if (ativo) {
          if (fator) {
            setMfaFactorId(fator.id);
            setMfaEstado('desafiar');
          } else {
            setMfaEstado('liberado');
          }
        }
      } catch (e) {
        console.error('Falha ao checar MFA no reset de senha:', e);
        if (ativo) setMfaEstado('liberado');
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (password.length < 6) {
      setErrorMessage('A senha precisa ter pelo menos 6 caracteres.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMessage('As senhas não coincidem.');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        setErrorMessage(error.message);
      } else {
        setSuccessMessage('Senha redefinida com sucesso. Redirecionando para o login…');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (error) {
      setErrorMessage('Ocorreu um erro desconhecido. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  if (mfaEstado === 'checando') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-rn-bg text-rn-text-soft">
        Verificando…
      </div>
    );
  }

  if (mfaEstado === 'desafiar') {
    return (
      <MfaChallenge factorId={mfaFactorId} onSucesso={() => setMfaEstado('liberado')} />
    );
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Redefinir Senha</title>
      </Helmet>

      <div className="flex items-center justify-center min-h-screen bg-rn-bg px-4">
        <Card className="w-full max-w-md p-8" hover={false}>
          <h2 className="text-2xl font-semibold text-center mb-2 text-rn-navy">Redefinir senha</h2>
          <p className="text-center text-rn-text-soft mb-6">Escolha uma nova senha para sua conta.</p>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-rn-navy">
                Nova senha
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="mt-1 block w-full px-3 py-2 border border-rn-line rounded-md shadow-sm placeholder-rn-text-soft focus:outline-none focus:ring-2 focus:ring-rn-orange-text focus:border-rn-orange-text"
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-rn-navy">
                Confirmar nova senha
              </label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                className="mt-1 block w-full px-3 py-2 border border-rn-line rounded-md shadow-sm placeholder-rn-text-soft focus:outline-none focus:ring-2 focus:ring-rn-orange-text focus:border-rn-orange-text"
              />
            </div>

            {errorMessage && <Alert type="error" message={errorMessage} />}
            {successMessage && <Alert type="success" message={successMessage} />}

            <Button type="submit" className="w-full" disabled={loading} aria-label="Salvar nova senha">
              {loading ? 'Salvando...' : 'Salvar nova senha'}
            </Button>
          </form>
        </Card>
      </div>
    </>
  );
};

export default ResetPassword;
